/**
 * Which pose a man is in this frame, read from the sim state the renderer can already see:
 * `sinceShot` for recoil, `staggerTicks` for the stagger, `action` for the ladder, the enemy
 * `state` for the wounded and the dead. What the state cannot say - when a throw or a fall
 * BEGAN - the caller remembers from the events it was handed (`Fall`, `OneShot`).
 */

import { weaponUp } from '../../sim/enemies/specials';
import { RANGER } from '../../sim/tuning';
import { type Enemy, type Ranger, TICK_RATE } from '../../sim/types';
import {
  BLAST_SECONDS, CRUMPLE_SECONDS, type Motion, MOTION_SECONDS,
} from '../characters/poses';
import { flinchAt, kickAt } from '../characters/poseActions';
import type { SoldierState } from '../characters/soldier';

/** A fall the renderer saw begin. Without one (a resumed mission, a body met already down) he simply lies there. */
export interface Fall {
  /** Render time, seconds. */
  start: number;
  blast: boolean;
  /** Blast only: the way he is turned to be thrown AWAY from it, radians; else null. */
  facing: number | null;
}

/** A throw or a knife thrust in progress. */
export interface OneShot {
  motion: 'throw' | 'stab';
  /** Render time at which `age` was zero (already backdated to the release for a throw). */
  start: number;
}

/** A knocked-down man starts getting up this long before the sim has him back on his feet. */
const GET_UP_SECONDS = 0.45;
/** With the rifle still warm he keeps it in the shoulder rather than dropping to the carry. */
const AIM_LINGER_TICKS = 90;

const active = (shot: OneShot | null, time: number): shot is OneShot =>
  shot !== null && time - shot.start < MOTION_SECONDS[shot.motion];

/** The corpse, by way of the fall if it was seen and is not yet over. */
function fallen(s: SoldierState, fall: Fall | null, time: number): void {
  const age = fall ? time - fall.start : Number.POSITIVE_INFINITY;
  const seconds = fall?.blast ? BLAST_SECONDS : CRUMPLE_SECONDS;
  if (!fall || age >= seconds) {
    s.motion = 'dead';
    return;
  }
  s.motion = fall.blast ? 'deathBlast' : 'deathCrumple';
  s.age = age;
}

/**
 * Standing poses shared by both sides: on the move, the gait with recoil and stagger laid over it;
 * standing still, the stagger or the recoil as a pose of its own. `hitAge` is seconds since he was struck, or -1.
 */
function upright(s: SoldierState, moving: Motion | null, kick: number, hitAge: number, sinceShot: number): boolean {
  const stagger = hitAge >= 0 ? flinchAt(hitAge) : 0;
  if (moving) {
    s.motion = moving;
    s.kick = kick;
    s.flinch = stagger;
    return true;
  }
  if (stagger > 0) {
    s.motion = 'hit';
    s.age = hitAge;
    return true;
  }
  if (kick > 0) {
    s.motion = 'fire';
    s.age = sinceShot;
    return true;
  }
  return false;
}

/** `s` must have had its overlays cleared (`Soldier.clearOverlays`). */
export function poseRanger(s: SoldierState, r: Ranger, time: number, shot: OneShot | null, fall: Fall | null): void {
  if (r.status === 'kia') return fallen(s, fall, time);
  if (r.status === 'captured') {
    s.motion = 'surrender';
    return;
  }
  if (r.action === 'board') {
    s.motion = 'board';
    s.age = r.actionTicks / TICK_RATE;
    return;
  }
  const sinceShot = r.sinceShot / TICK_RATE;
  const kick = kickAt(sinceShot);
  if (r.stance === 'crawl') {
    s.motion = kick > 0 ? 'fireProne' : 'crawl';
    s.age = sinceShot;
    return;
  }
  if (active(shot, time)) {
    s.motion = shot.motion;
    s.age = time - shot.start;
    return;
  }
  const hitAge = r.staggerTicks > 0 ? (RANGER.staggerTicks - r.staggerTicks) / TICK_RATE : -1;
  if (upright(s, r.running ? 'run' : r.moving ? 'walk' : null, kick, hitAge, sinceShot)) return;
  // A grenade or smoke in the hand: he stands with the arm cocked, which is the throw's first drawing.
  if (r.weapon === 'grenade' || r.weapon === 'smoke') s.motion = 'throw';
  else s.motion = r.sinceShot < AIM_LINGER_TICKS ? 'aim' : 'idle';
}

export function poseEnemy(
  s: SoldierState, e: Enemy, alert: number, time: number, shot: OneShot | null, fall: Fall | null,
): void {
  if (e.state === 'dead') return fallen(s, fall, time);
  if (e.state === 'wounded') {
    s.motion = 'wounded';
    s.age = fall ? time - fall.start : Number.POSITIVE_INFINITY;
    // Quiet once a comrade has him, or has dragged him clear; until then he is calling for help.
    s.vigour = e.stabilised || e.carriedBy !== -1 || e.draggedByRanger ? 0 : 1;
    return;
  }
  if (e.state === 'down') {
    // Knocked flat, then back up: the crumple played forward from the hit and backward into the get-up.
    const since = fall ? (time - fall.start) / CRUMPLE_SECONDS : 1;
    const left = e.stateTicks / TICK_RATE / GET_UP_SECONDS;
    s.motion = 'deathCrumple';
    s.age = CRUMPLE_SECONDS * Math.max(0, Math.min(1, since, left));
    return;
  }
  if (active(shot, time)) {
    s.motion = shot.motion;
    s.age = time - shot.start;
    return;
  }
  // Jet lit, or tube coming up to fire: the braced stance, even in mid-stride.
  if (weaponUp(e)) {
    s.motion = 'aim';
    return;
  }
  const sinceShot = e.sinceShot / TICK_RATE;
  const kick = kickAt(sinceShot);
  const urgent = e.state === 'runner' || e.state === 'flee' || e.state === 'combat';
  const stride: Motion = urgent || (e.state === 'investigate' && alert >= 2) ? 'run' : 'walk';
  if (upright(s, e.moving ? stride : null, kick, -1, sinceShot)) return;
  const rifleman = e.kind !== 'flamethrower' && e.kind !== 'rocketTrooper';
  if (rifleman && e.burstLeft > 0) {
    // Between the rounds of a burst he stays braced behind the rifle.
    s.motion = 'fire';
    s.age = sinceShot;
  } else s.motion = e.state === 'combat' && e.seesRanger ? 'aim' : 'idle';
}
